import { FC, useCallback } from 'react';
import graphql from 'babel-plugin-relay/macro';
import {
  fetchQuery,
  useFragment,
  useMutation,
  useRelayEnvironment,
} from 'react-relay';
import { Formik } from 'formik';

import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import InputAdornment from '@mui/material/InputAdornment';
import Form from 'components/Form';
import TextField from 'components/TextField';
import LimitedTextField from 'components/LimitedTextField';

import { debounce, useErrorHandler } from 'lib';
import { SeoTab_seoable$key } from '__generated__/SeoTab_seoable.graphql';
import { SeoTabMutation } from '__generated__/SeoTabMutation.graphql';
import {
  SeoableType,
  SeoTabCheckSlugQuery,
  SeoTabCheckSlugQuery$data,
} from '__generated__/SeoTabCheckSlugQuery.graphql';
import Link from '@mui/material/Link';

const check = graphql`
  query SeoTabCheckSlugQuery(
    $slug: String!
    $type: SeoableType!
    $exclude: [ID!]
  ) {
    slugExists(slug: $slug, type: $type, exclude: $exclude) {
      seoable {
        id
      }
    }
  }
`;

type FormFields = {
  slug: string;
  metaTitle: string;
  metaDescription: string;
};

type Props = {
  seoable: SeoTab_seoable$key;
  type: SeoableType;
  urlPrefix?: string;
  seoableUrlPrefix?: string;
};

const SeoTab: FC<Props> = ({ type, urlPrefix, seoableUrlPrefix, ...props }) => {
  const errorHandler = useErrorHandler();
  const environment = useRelayEnvironment();

  const seoable = useFragment(
    graphql`
      fragment SeoTab_seoable on Seoable {
        id

        seo {
          id
          slug
          metaTitle
          metaDescription
        }
      }
    `,
    props.seoable
  );

  const [update, loading] = useMutation<SeoTabMutation>(graphql`
    mutation SeoTabMutation($id: ID!, $input: UpdateSeoInput!) {
      updateSeo(id: $id, input: $input) {
        id
        slug
        metaTitle
        metaDescription
      }
    }
  `);

  const { id, slug, metaTitle, metaDescription } = seoable.seo;

  const initial: FormFields = {
    slug,
    metaTitle: metaTitle ?? '',
    metaDescription: metaDescription ?? '',
  };

  const handleSubmit = useCallback(
    (input: FormFields) => {
      update({
        variables: { id, input },

        ...errorHandler({
          successMessage: 'Išsaugota',
        }),
      });
    },
    [errorHandler, id, update]
  );

  const validate = useCallback(
    ({ slug }: FormFields) =>
      new Promise((resolve, reject) => {
        let receivedData: SeoTabCheckSlugQuery$data;

        const vars = {
          slug,
          type,
          exclude: [seoable.id],
        };

        debounce(() => {
          fetchQuery<SeoTabCheckSlugQuery>(environment, check, vars).subscribe({
            complete: () => {
              resolve(
                receivedData.slugExists
                  ? {
                      slug: (
                        <span>
                          Toks URL jau egzistuoja{' '}
                          {seoableUrlPrefix && (
                            <Link
                              href={`${seoableUrlPrefix}/${receivedData.slugExists.seoable.id}#seo`}
                              target="_blank">
                              <strong>čia</strong>
                            </Link>
                          )}
                        </span>
                      ),
                    }
                  : {}
              );
            },

            error: (error: Error) => {
              reject(error);
            },

            next: data => {
              receivedData = data;
            },
          });
        }, 500);
      }),
    [environment, seoable.id, seoableUrlPrefix, type]
  );

  return (
    <Formik<FormFields>
      initialValues={initial}
      validate={validate}
      onSubmit={handleSubmit}>
      <Form loading={loading}>
        <Paper sx={{ p: 2 }}>
          <Stack spacing={4}>
            <TextField
              label="Meta Title"
              name="metaTitle"
              limit={60}
              // @ts-expect-error
              component={LimitedTextField}
            />

            <TextField
              label="Meta Description"
              name="metaDescription"
              limit={300}
              // @ts-expect-error
              component={LimitedTextField}
            />

            <TextField
              label="URL"
              name="slug"
              InputProps={{
                startAdornment: urlPrefix && (
                  <InputAdornment position="start">{urlPrefix}</InputAdornment>
                ),
              }}
            />
          </Stack>
        </Paper>
      </Form>
    </Formik>
  );
};

export default SeoTab;
